import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import actions from "actions";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import ClientSearchBar from "./ClientSearchBar";
import cookie from "react-cookies";
import { useTheme } from "@emotion/react";
import MessagesList from "./MessagesList";
import { SORT_OPTIONS } from "constants/sortMessagesOptions";
import { Box, Stack, Paper } from "@mui/material";
import SortMenuButton from "./SortMenuButton";
import ANLoadingScreen from "elements/ANLoadingScreen";
import MessagesChat from "../MessagesChat";
import { PencilSimpleLine } from "@phosphor-icons/react";
import { useGetChatPreviews } from "hooks/api/messages/useGetChatPreviews";
import { useUpdateMessagesParticipant } from "hooks/api/messages/useUpdateMessagesParticipant";

const Messages = () => {
  const currentUserId = cookie.load("userId");
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const redirectMsgId = params.get("msg_id");
  const redirectChatId = params.get("chat_id");

  const theme = useTheme();

  const [selectedTeamMemberId, setSelectedTeamMemberId] = useState(null);
  const [sortOption, setSortOption] = useState(SORT_OPTIONS.RECENT);
  const { chatPreviews, previewsLoading, refetchPreviews } = useGetChatPreviews(
    currentUserId,
    9000
  );
  const [externalRefetchNotificationFn, setExternalRefetchNotificationFn] = useState(null);
  const { updateParticipant } = useUpdateMessagesParticipant();

  // A set of chat IDs that were recently read, used to suppress the unread badge in the UI temporarily
  const recentlyReadChats = useRef(new Set());
  // A map of chatId -> timeout ID, used to clear the temporary read state after 4 seconds
  const chatTimeouts = useRef(new Map());

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(
      actions.setPageDetails({
        pageName: "Messages",
        currentPage: "messages",
        menu: "messagesHome",
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (!redirectChatId) return;

    setSelectedTeamMemberId(redirectChatId);
  }, [redirectChatId, redirectMsgId]);

  /**
   * Temporarily marks a chat as recently read to suppress the unread badge in the UI.
   * This improves UX by preventing a flash of the badge due to async lag
   */
  const markChatAsRecentlyRead = useCallback((chatId) => {
    if (chatTimeouts.current.has(chatId)) {
      clearTimeout(chatTimeouts.current.get(chatId));
      chatTimeouts.current.delete(chatId);
    }

    recentlyReadChats.current.add(chatId);

    const timeoutId = setTimeout(() => {
      recentlyReadChats.current.delete(chatId);
      chatTimeouts.current.delete(chatId);
    }, 4000);

    chatTimeouts.current.set(chatId, timeoutId);
  }, []);

  const handleTeamMemberSelect = (newValue) => {
    //Only mark a chat as recently read on navigation away from the chat
    if (selectedTeamMemberId && selectedTeamMemberId !== newValue) {
      markChatAsRecentlyRead(selectedTeamMemberId);
    }
    setSelectedTeamMemberId(newValue);
  };

  const handleSortSelection = (option) => {
    setSortOption(option);
  };

  //sorts the chats based on a SORT_OPTION before passing into MessagesList component
  const sortedChats = useMemo(() => {
    if (!chatPreviews) return [];

    return [...chatPreviews].sort((a, b) => {
      switch (sortOption) {
        case SORT_OPTIONS.TEAM_MEMBER: {
          const nameCompare = a.member_full_name.localeCompare(b.member_full_name);
          if (nameCompare !== 0) return nameCompare;
          // stable id to keep consistent order between chats with same name
          return a.chat_id - b.chat_id;
        }
        case SORT_OPTIONS.RECENT:
        default: {
          const aDate = a.last_message_date ? new Date(a.last_message_date).getTime() : null;
          const bDate = b.last_message_date ? new Date(b.last_message_date).getTime() : null;

          if (aDate === null && bDate === null) {
            return a.chat_id - b.chat_id; // use stable ID to keep consistent order between chats with no last message
          }
          if (aDate === null) return 1;
          if (bDate === null) return -1;

          if (bDate !== aDate) return bDate - aDate;
          // use stable id to keep consistent order between chats with same date
          return a.chat_id - b.chat_id;
        }
      }
    });
  }, [chatPreviews, sortOption]);

  return !previewsLoading ? (
    <Stack direction="row" gap="48px" sx={{ height: "100%", padding: "24px" }}>
      <Stack direction="column" gap="24px" minWidth="395px" maxWidth="395px">
        <Stack direction="row" gap="8px">
          <ClientSearchBar
            userId={currentUserId}
            onSelectMemberId={handleTeamMemberSelect}
            placeholder="Search and start a conversation"
            endAdornment={
              <PencilSimpleLine
                color={theme.palette.text.secondary}
                style={{ position: "absolute", right: "10px" }}
              />
            }
          />
          <SortMenuButton onSortChange={handleSortSelection} />
        </Stack>
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            //Hide scrollbar
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          <MessagesList
            openChatId={selectedTeamMemberId}
            messagePreviews={sortedChats}
            onSelectChat={handleTeamMemberSelect}
            recentlyReadChats={recentlyReadChats.current}
            externalNotificationRefetch={externalRefetchNotificationFn}
            userId={currentUserId}
            refetchPreviews={refetchPreviews}
            updateParticipantMutation={updateParticipant}
            selectedTeamMemberId={selectedTeamMemberId}
          />
        </Box>
      </Stack>

      <Paper
        elevation={2}
        sx={{
          width: 600,
          height: "100%",
        }}
      >
        <MessagesChat
          memberId={selectedTeamMemberId}
          refetchPreviews={refetchPreviews}
          externalTriggerRefetch={(refetchFn) => setExternalRefetchNotificationFn(() => refetchFn)}
          redirectMsgId={redirectMsgId}
        />
      </Paper>
    </Stack>
  ) : (
    <ANLoadingScreen />
  );
};

export default Messages;
